import RechercheVoyage from "./recherche-voyage";
import JeSaisOuJeVais from "./je-sais-ou-je-vais";
import { clearParamsFromCurrentUrl } from "utils/url";
import { RootState } from "store";
import { useSelector } from "react-redux";
import SearchCriteria from "models/TransferModels/SearchCriteria";
import { Box, Container, Tab, Tabs, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchCriteriaFromQuery } from "hooks/useSearchCriteriaFromQuery";
import classes from "components/style";
import { a11yProps, TabPanel } from "helpers/tabPanels";
import BlocTroisMots from "./bloc-trois-mots";
import TopCountries from "./top-countries";
import Country from "models/Country";
import StayType from "models/StayType";
import Duration from "models/Duration";
import Budget from "models/Budget";
import Transport from "models/Transport";
import { mapByIdOrName } from "utils/array";
import AdSlot from "components/AdSlot";
import PublicProfilesCarousel from "./PublicProfilesCarousel";
import TravelIntentBlock from "./travel-intent-block";

type SearchLS = {
  destinations?: Partial<Country>[];
  continents?: string[];
  durations?: (Partial<Duration> | number | string)[];
  stayTypes?: (Partial<StayType> | number | string)[];
  budgets?: (Partial<Budget> | number | string)[];
  transports?: (Partial<Transport> | number | string)[];
  toPays?: boolean;
  toProfils?: boolean;
};

const parseContinentsFromSearch = (search: string): string[] => {
  if (!search) return [];
  const sp = new URLSearchParams(search);
  const raw = sp.getAll("continents").flatMap((v) => v.split(","));
  return raw
    .map((v) => v.trim())
    .filter(Boolean);
};

const Home: FC = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [value, setValue] = useState<number>(pathname === "/je-cherche-un-voyage" ? 1 : 0);

  const continentsFromQuery = useMemo(() => parseContinentsFromSearch(search), [search]);

  const { budgets, stayTypes, durations, transports, countries } = useSelector(
    (state: RootState) => state.globalSlice,
  );

  const prefilled = useSearchCriteriaFromQuery();
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria | undefined>(undefined);

  const criteriaFromLocalStorage = useMemo<SearchCriteria | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    try {
      const raw = window.localStorage.getItem("SEARCH");
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as SearchLS;
      const resolved: SearchCriteria = ({
        destinations: mapByIdOrName(countries, parsed.destinations as any),
        continents: (parsed.continents ?? []) as any,
        durations: mapByIdOrName(durations, parsed.durations),
        stayTypes: mapByIdOrName(stayTypes, parsed.stayTypes),
        budgets: mapByIdOrName(budgets, parsed.budgets),
        transports: mapByIdOrName(transports, parsed.transports),
        from: undefined as any,
        to: undefined as any,
      }) as any;
      const hasAny =
        resolved.destinations.length > 0 ||
        ((resolved as any).continents?.length ?? 0) > 0 ||
        resolved.stayTypes.length > 0 ||
        resolved.durations.length > 0 ||
        resolved.budgets.length > 0 ||
        resolved.transports.length > 0;
      return hasAny ? resolved : undefined;
    } catch {
      return undefined;
    }
  }, [budgets, stayTypes, durations, transports, countries]);

  useEffect(() => {
    if (pathname === "/je-cherche-un-voyage") setValue(1);
    else if (pathname === "/") setValue(0);
  }, [pathname]);

  useEffect(() => {
    clearParamsFromCurrentUrl();
  }, []);

  useEffect(() => {
    const refsLoaded =
      budgets.length > 0 &&
      durations.length > 0 &&
      countries.length > 0 &&
      stayTypes.length > 0 &&
      transports.length > 0;

    if (!refsLoaded) return;

    if (!searchCriteria) {
      const mergeContinents = (base: SearchCriteria): SearchCriteria => {
        if (!continentsFromQuery || continentsFromQuery.length === 0) return base;
        return { ...(base as any), continents: continentsFromQuery } as any;
      };

      if (prefilled) setSearchCriteria(mergeContinents(prefilled));
      else if (criteriaFromLocalStorage) setSearchCriteria(mergeContinents(criteriaFromLocalStorage));
    }
  }, [
    budgets,
    durations,
    countries,
    transports,
    stayTypes,
    prefilled,
    criteriaFromLocalStorage,
    continentsFromQuery,
    searchCriteria,
  ]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    navigate(newValue === 0 ? "/" : "/je-cherche-un-voyage");
  };

  return (
    <>
      <AdSlot zone='HOME_HEADER_TOP' />
      <Container maxWidth={false} sx={[classes.homeMainContainer, classes.root, classes.calendrier]}>
        <Container sx={classes.homeContainer}>
          <Box sx={classes.bigTitle}>
            <Typography variant="subtitle1">
              Trouve un voyage, un compagnon ou les deux
            </Typography>
          </Box>
          <Box sx={classes.mainTabsPanel}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="recherche voyage tabs"
              TabIndicatorProps={{ style: { backgroundColor: "transparent" } }}

            >
              <Tab sx={classes.tabBtn} label="Je sais où je vais" {...a11yProps(1)} style={{ textDecoration: value === 1 ? null : 'underline' }} />
              <Tab sx={classes.tabBtn} label="Je cherche un voyage" {...a11yProps(0)} style={{ textDecoration: value === 0 ? null : 'underline' }} />
            </Tabs>

            <TabPanel value={value} index={1}>
              <Box sx={classes.onlyDesktop}>
                <RechercheVoyage searchCriteria={searchCriteria} />
              </Box>
              <Box sx={classes.onlyMobile}>
                <RechercheVoyage searchCriteria={searchCriteria} />
              </Box>
            </TabPanel>
            <TabPanel value={value} index={0}>
              <Box sx={classes.onlyDesktop}>
                <JeSaisOuJeVais searchCriteria={searchCriteria} />
              </Box>
              <Box sx={classes.onlyMobile}>
                <JeSaisOuJeVais searchCriteria={searchCriteria} />
              </Box>
            </TabPanel>
          </Box>
        </Container>
      </Container>

      <AdSlot zone='HOME_SEARCH_BOT' />
      <TravelIntentBlock />
      <PublicProfilesCarousel />

      <Container
        disableGutters
        sx={{ ...classes.root, ...classes.calendrier, display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <TopCountries />
      </Container>

      <AdSlot zone='HOME_DESTINATION_BOT' />

      <Container
        disableGutters
        sx={{ ...classes.calendrier, display: "flex", justifyContent: "center", alignItems: "center", paddingBottom: '50px' }}
      >
        <BlocTroisMots />
      </Container>

      <AdSlot zone='HOME_FOODER_TOP' />
    </>
  );
};

export default Home;
